import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Droplets, AlertTriangle, ExternalLink } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import {
  type PropertyWaterPolicy,
  type DayAbbr,
  resolvePropertyRule,
  resolvePropertyAllowedDays,
} from '../lib/waterRestrictions';

interface Props {
  jobSiteId: string;
  /** Bubbles the fetched policy up so a parent (e.g. controller form) can validate against it. */
  onLoaded?: (policy: PropertyWaterPolicy | null) => void;
  className?: string;
}

export function WaterRestrictionPolicyCard({ jobSiteId, onLoaded, className }: Props) {
  const { t } = useTranslation('common');
  const { t: tInsp } = useTranslation('inspection');
  const [policy, setPolicy] = useState<PropertyWaterPolicy | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient
      .get<PropertyWaterPolicy>(`/api/job-sites/${jobSiteId}/water-restriction`)
      .then((p) => {
        if (!active) return;
        setPolicy(p);
        onLoaded?.(p);
      })
      .catch((err) => {
        console.error('Failed to load water restriction policy:', err);
        if (active) onLoaded?.(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSiteId]);

  const dayLabel = (d: DayAbbr) => tInsp(`schedule.day${d}`);

  if (loading) {
    return (
      <div className={`rounded-lg border border-line bg-subtle p-4 text-sm text-theme-secondary ${className ?? ''}`}>
        {t('waterRestrictions.loading', 'Loading water restriction policy…')}
      </div>
    );
  }

  const rule = resolvePropertyRule(policy);

  // No city schedule configured for this property's city.
  if (!policy?.schedule || !rule) {
    return (
      <div className={`rounded-lg border border-line bg-subtle p-4 ${className ?? ''}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-theme-primary">
          <Droplets size={16} /> {t('waterRestrictions.policyTitle', 'Water Restriction Policy')}
        </div>
        <p className="mt-1 text-sm text-theme-secondary">
          {policy?.city
            ? t('waterRestrictions.notSetForCity', { city: policy.city, defaultValue: `No policy is configured for ${policy.city}.` })
            : t('waterRestrictions.notSet', 'No water restriction policy is configured for this property.')}
        </p>
      </div>
    );
  }

  const sched = policy.schedule;
  const allowed = resolvePropertyAllowedDays(policy);
  const classLabel =
    policy.property_class === 'residential'
      ? t('waterRestrictions.residential', 'Residential')
      : t('waterRestrictions.nonResidential', 'Non-residential / HOA');

  let daysText: string;
  if (rule.assignment_type === 'none') {
    daysText = t('waterRestrictions.anyDays', { n: rule.max_days_per_week ?? 2, defaultValue: `Any ${rule.max_days_per_week ?? 2} days per week (your choice)` });
  } else if (allowed == null) {
    daysText = t('waterRestrictions.couldNotResolveDays', 'Depends on the street address — could not determine automatically; verify.');
  } else if (allowed.length === 0) {
    daysText = '—';
  } else {
    daysText = allowed.map(dayLabel).join(', ');
  }

  return (
    <div className={`rounded-lg border border-line bg-subtle p-4 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-theme-primary">
          <Droplets size={16} className="text-sky-500" /> {t('waterRestrictions.policyTitle', 'Water Restriction Policy')}
          {policy.city && <span className="font-normal text-theme-secondary">· {policy.city}</span>}
        </div>
        <span className="rounded-full border border-line bg-surface px-2 py-0.5 text-xs font-medium text-theme-secondary">{classLabel}</span>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-theme-primary sm:grid-cols-2">
        <div>
          <span className="font-semibold">{t('waterRestrictions.allowedDays', 'Allowed days')}:</span> {daysText}
        </div>
        <div>
          <span className="font-semibold">{t('waterRestrictions.noWaterWindow', 'No watering')}:</span>{' '}
          {rule.no_water_window ? `${rule.no_water_window.start}–${rule.no_water_window.end}` : t('waterRestrictions.noTimeLimit', 'No time limit')}
        </div>
        <div>
          <span className="font-semibold">{t('waterRestrictions.season', 'Season')}:</span>{' '}
          {rule.seasonal ? `${rule.seasonal.start} → ${rule.seasonal.end}` : t('waterRestrictions.yearRound', 'Year-round')}
        </div>
        <div>
          <span className="font-semibold">{t('waterRestrictions.maxDays', 'Max days/week')}:</span> {rule.max_days_per_week ?? '—'}
          {rule.drip_exempt && <span className="text-theme-secondary"> · {t('waterRestrictions.dripExempt', 'drip exempt')}</span>}
        </div>
      </div>

      {(sched.authority || sched.source_url || sched.last_verified) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-theme-secondary">
          {sched.authority && <span>{sched.authority}</span>}
          {sched.last_verified && (
            <span>{t('waterRestrictions.lastVerified', { date: sched.last_verified.slice(0, 10), defaultValue: `Verified ${sched.last_verified.slice(0, 10)}` })}</span>
          )}
          {sched.source_url && (
            <a href={sched.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline hover:text-theme-primary">
              {t('waterRestrictions.source', 'Source')} <ExternalLink size={11} />
            </a>
          )}
        </div>
      )}

      {(sched.authority_type === 'unknown' || (sched.notes && /verify/i.test(sched.notes))) && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{sched.notes || t('waterRestrictions.unknownProvider', 'Provider/rule unconfirmed — verify locally before relying on this.')}</span>
        </div>
      )}
    </div>
  );
}
